import React, { useContext, useEffect, useState } from 'react';
import { RouteComponentProps, useParams } from '@reach/router';
import { StyledMainContent } from '../Styled/StyledMainContent';
import { StyledColoredLine } from '../Styled';
import { MainContentHeader } from '../MainContentHeader';
import { ExemplarScreen } from '../ExemplarScreen';
import { BackLink } from '../BackLink';
import { ExemplarCombined, Proforma, RelatedObject } from '../../types';
import { WritingProforma } from '../WritingProforma';
import { getProforma, getStageColors, getAppropriateExemplarRoutes } from '../../utils';
import { CONSOLIDATING, DEVELOPING, EARLY, NOT_FOUND } from '../../constants';
import { StickedBottomWrapper, StyledExemplarAndNavigationWrapper } from '../Styled/StyledWrappers';
import { EditToolContainer } from '../EditToolContainer/EditToolContainer';
import { BottomNavigation } from '../BottomNavigation';
import { DEFAULT_BTN } from '../BottomNavigation/BottomNavigation';
import { capitalize } from 'lodash';
import { TEXT_PRESENTATION } from '../../constants/navigation';
import { EditToolContext } from '../../contexts/editToolContext';
import AuthContext from '../../contexts/authContext';
import { IAsset } from '../../constants/assets';
import { exemplarMainContentTitle } from '../../constants/exemplar';
import { ProformaContext } from '../../contexts/proformaContext';

interface IMainContent extends RouteComponentProps {
  assets: IAsset[];
  stage: string;
  page?: string;
  exemplar: ExemplarCombined;
  exemplarId: number;
  type?: string;
  addSavedToRelated: (relatedText: RelatedObject) => void;
}

export const MainContent = (props: IMainContent): JSX.Element => {
  const { assets, stage, page, exemplar, exemplarId, type, navigate, addSavedToRelated } = props;

  const [isActive, setActive] = useState(false);
  const [expanded, setExpand] = useState(0);
  const [prevBtnId, setActiveBtn] = useState(DEFAULT_BTN);
  const [content, setContent] = useState(<></>);
  const { screen } = useParams();
  const { isEditMode, setEditMode } = useContext(EditToolContext);
  const { setDefaultStage } = useContext(AuthContext);
  const { loadProforma } = useContext(ProformaContext);

  const isFullScreen = screen === 'full-screen';
  const currentPage = page || TEXT_PRESENTATION;
  const isProforma = getProforma();
  const showProforma = [EARLY, DEVELOPING, CONSOLIDATING].includes(stage);
  const isAllowedRoute = getAppropriateExemplarRoutes(stage).includes(currentPage);
  const query = `stage=${capitalize(stage)}&textType=${exemplar?.text_type}`;
  const { gradient: gradientColor, label: labelBgColor } = getStageColors(stage);

  useEffect(() => {
    setDefaultStage(stage);
    if (isEditMode) {
      setEditMode(false);
    }
  }, [exemplar]);

  useEffect(() => {
    isProforma && loadProforma(exemplar as Proforma);
  }, []);

  const handleEditClick = () => {
    setEditMode(!isEditMode);
  };

  const renderExemplar = () => {
    if (!isAllowedRoute) {
      navigate && navigate(`/${NOT_FOUND}`);

      return null;
    }

    if (isProforma) {
      return <WritingProforma proforma={exemplar as Proforma} addSavedToRelated={addSavedToRelated} />;
    }

    return (
      exemplar && (
        <ExemplarScreen
          exemplar={exemplar}
          exemplarId={exemplarId}
          mode={currentPage}
          type={type as string}
          stage={stage}
          isFullScreen={isFullScreen}
        />
      )
    );
  };

  const renderContentWithBottomNavigation = (): JSX.Element => (
    <>
      <StyledMainContent
        isFullScreen={isFullScreen}
        isProforma={isProforma}
        isBottomSectionExpanded={!!expanded}
        isEditMode={isEditMode}
      >
        {renderExemplar()}
      </StyledMainContent>
      <StickedBottomWrapper isFixed={(!isFullScreen && !expanded) as boolean}>
        <EditToolContainer
          mode={page as string}
          onClose={handleEditClick}
          isFullScreen={isFullScreen as boolean}
          stage={stage}
          exemplar={exemplar}
          exemplarId={+exemplarId}
          exemplarType={type}
        />
        <BottomNavigation
          assets={assets}
          navigate={navigate}
          page={page}
          textType={exemplar?.text_type}
          focus={exemplar && 'teacher_focus' in exemplar ? exemplar?.teacher_focus : undefined}
          isActive={isActive}
          setActive={setActive}
          expanded={expanded}
          setExpand={setExpand}
          prevBtnId={prevBtnId}
          setActiveBtn={setActiveBtn}
          onEditClick={handleEditClick}
          setContent={setContent}
          content={content}
          isProforma={showProforma}
          query={query}
          staticPage={exemplar?.page}
        />
      </StickedBottomWrapper>
    </>
  );

  return (
    <>
      {!isFullScreen && (
        <BackLink
          backTo={`/exemplars/${stage}`}
          collection={exemplar && 'collection' in exemplar ? exemplar?.collection : stage}
        />
      )}
      {!isFullScreen && <StyledColoredLine color={gradientColor} />}
      <MainContentHeader
        headerText={currentPage}
        headingText={exemplarMainContentTitle(exemplar)}
        showHeading={!isFullScreen}
        labelBgColor={labelBgColor}
        showShadow={!isProforma}
      />
      {isFullScreen ? (
        <StyledExemplarAndNavigationWrapper>{renderContentWithBottomNavigation()}</StyledExemplarAndNavigationWrapper>
      ) : (
        renderContentWithBottomNavigation()
      )}
    </>
  );
};
